import { useState } from 'react';
import emailjs from '@emailjs/browser';
import { motion } from 'framer-motion';
import { translations } from '../locales/translations';

function Contact() {
  const [lang, setLang] = useState('ko');
  const t = translations[lang];
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus('sending');

    const templateParams = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      message: formData.message,
      reply_to: formData.email,
    };

    try {
      // 관리자에게 이메일 전송
      const adminResponse = await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          ...templateParams,
          to_name: 'HanSomang Church Admin',
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );

      if (adminResponse.status === 200) {
        setStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        setStatus('error');
      }
    } catch (error) {
      console.error('EmailJS Error:', error);
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const handleMessageChange = (e) => {
    const message = e.target.value;
    if (message.length <= 500) {
      setFormData({ ...formData, message: message });
    }
  };

  return (
    <div className="min-h-screen pt-20 pb-16 px-4 bg-primary/10">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-lg mx-auto bg-white rounded-xl shadow-lg p-6 md:p-8"
      >
        <h1 className="text-3xl md:text-4xl font-title text-text-navy text-center mb-6">
          {t.contact.title}
        </h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
            >
              <label className="block text-sm font-medium text-text-dark mb-2">
                {t.contact.email}
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="w-full px-4 py-3 rounded-lg border border-neutral focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all"
                placeholder={lang === 'ko' ? '이메일 주소를 입력해주세요' : 'Enter your email address'}
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <label className="block text-sm font-medium text-text-dark mb-2">
                {t.contact.name}
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="w-full px-4 py-3 rounded-lg border border-neutral focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all"
                placeholder={lang === 'ko' ? '이름을 입력해주세요' : 'Enter your name'}
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
            >
              <label className="block text-sm font-medium text-text-dark mb-2">
                {t.contact.phone}
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-4 py-3 rounded-lg border border-neutral focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all"
                placeholder={lang === 'ko' ? '전화번호를 입력해주세요' : 'Enter your phone number'}
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
            >
              <label className="block text-sm font-medium text-text-dark mb-2">
                {t.contact.message}
                <span className="text-sm text-gray-500 ml-2">
                  ({formData.message.length}/500)
                </span>
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleMessageChange}
                required
                maxLength={500}
                rows={6}
                className="w-full px-4 py-3 rounded-lg border border-neutral focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all"
                placeholder={lang === 'ko' ? '문의하실 내용을 입력해주세요 (최대 500자)' : 'Enter your message (max 500 characters)'}
              />
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-center"
          >
            <button
              type="submit"
              disabled={loading}
              className="w-full md:w-auto px-8 py-3 bg-secondary hover:bg-accent text-white rounded-lg transition-all transform hover:scale-105 disabled:opacity-50 disabled:hover:scale-100"
            >
              {loading ? (
                lang === 'ko' ? '전송 중...' : 'Sending...'
              ) : (
                lang === 'ko' ? '문의하기' : 'Send Message'
              )}
            </button>
          </motion.div>
        </form>

        {status === 'success' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mt-6 p-4 bg-green-100 text-green-700 rounded-lg text-center"
          >
            {lang === 'ko' 
              ? '메시지가 성공적으로 전송되었습니다. 빠른 시일 내에 답변 드리겠습니다.' 
              : 'Message sent successfully. We will respond to you soon.'}
          </motion.div>
        )}

        {status === 'error' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mt-6 p-4 bg-red-100 text-red-700 rounded-lg text-center"
          >
            {lang === 'ko'
              ? '메시지 전송에 실패했습니다. 잠시 후 다시 시도해주세요.'
              : 'Failed to send message. Please try again later.'}
          </motion.div>
        )}
      </motion.div>
    </div>
  );
}

export default Contact;